import SettingMer from '@/libs/settingMer';
import { getToken } from '@/utils/auth';
import { cityDataLst } from '@/api/system';
import { ElMessage } from 'element-plus';
import type { Rule } from '@form-create/element-ui';

interface CityNode {
  level?: number;
  value?: number;
}

interface CityListItem {
  name: string;
  id: number;
  hasChildren?: boolean;
}

interface CascaderOption {
  label: string;
  value: number;
  leaf: boolean;
}

const cacheMap = new Map<number, CascaderOption[]>();

/**
 * 懒加载城市列表
 */
export async function lazyLoadCity(
  node: CityNode,
  resolve: (data: CascaderOption[]) => void,
): Promise<void> {
  const cityId = node.level === 0 ? 0 : (node.value ?? 0);

  if (!cacheMap.has(cityId)) {
    const res = (await cityDataLst(cityId)) as { data: CityListItem[] };
    const cityList = res.data.map(item => ({
      label: item.name,
      value: item.id,
      leaf: !item.hasChildren,
    }));
    cacheMap.set(cityId, cityList);
  }
  const data = cacheMap.get(cityId) ?? [];

  // 必须异步执行 resolve，否则级联选择器 UI 不会更新
  setTimeout(() => {
    resolve(JSON.parse(JSON.stringify(data)) as CascaderOption[]);
  });
}

/**
 * 获取 form-create 上传组件配置
 */
export function getUploadProps(limit = 10, uploadType = 'image', accept = 'image/*') {
  return {
    limit,
    uploadType,
    accept,
    action: `${SettingMer.https}/upload/image/0/file`,
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    beforeUpload(file: File) {
      const isValidFile = new RegExp(accept.replace(/\*/g, '.*')).test(file.type);
      if (!isValidFile) {
        ElMessage.error('文件后缀不合法!');
        return false;
      }
      return true;
    },
    onSuccess(res: { status?: number; data?: { src?: string }; message?: string }, file: { url?: string }, fileList: { pop: () => void }[]) {
      if (res.status === 200) {
        file.url = res.data?.src;
      } else {
        fileList.pop();
        ElMessage.error(res.message || '上传失败');
      }
    },
  };
}

const SYSTEM_FORM_TYPE_MAP: Record<string, string> = {
  checkboxs: 'checkbox',
  citys: 'cascader',
  dates: 'datePicker',
  dateranges: 'datePicker',
  radios: 'radio',
  selects: 'select',
  texts: 'input',
  times: 'timePicker',
  timeranges: 'timePicker',
  uploadPicture: 'upload',
};

const INPUT_TYPE_MAP: Record<number, string> = {
  0: 'text',
  1: 'tel',
  2: 'id_card',
  3: 'email',
  4: 'number',
};

interface SystemFormField {
  name: string;
  titleConfig: { value: string };
  titleShow: { val: unknown };
  wordsConfig?: { list: { val: string }[] };
  valConfig?: { tabVal: number };
  tipConfig?: { value: string };
  numConfig?: { val: number };
}

/**
 * 将系统表单配置转换为 form-create 规则
 */
export function systemFormToFormCreateRule(systemForm: Record<string, SystemFormField>): Rule[] {
  return Object.values(systemForm)
    .map(value => {
      const type = SYSTEM_FORM_TYPE_MAP[value.name];
      if (!type) return null;

      const options: Rule & { rawType?: string } = {
        type,
        rawType: value.name,
        title: value.titleConfig.value,
        field: value.titleConfig.value,
        $required: !!value.titleShow.val,
      };

      switch (value.name) {
        case 'checkboxs':
        case 'radios':
        case 'selects':
          options.options = (value.wordsConfig?.list ?? []).map(item => ({
            label: item.val,
            value: item.val,
          }));
          break;
        case 'citys':
          options.props = {
            props: {
              lazy: true,
              lazyLoad: lazyLoadCity,
            },
          };
          break;
        case 'dateranges':
          options.props = {
            type: 'daterange',
            rangeSeparator: '~',
          };
          break;
      case "dates": // 日期选择器
          options.props = {
              type: "date"
          };
          break;
        case 'texts':
          options.props = {
            type: INPUT_TYPE_MAP[value.valConfig?.tabVal ?? 0] ?? 'text',
            placeholder: value.tipConfig?.value,
          };
          break;
        case 'times':
          options.props = {
            format: 'HH:mm',
            valueFormat: 'HH:mm',
          };
          break;
        case 'timeranges':
          options.props = {
            format: 'HH:mm',
            valueFormat: 'HH:mm',
            isRange: true,
            rangeSeparator: '~',
          };
          break;
        case 'uploadPicture':
          options.props = getUploadProps(value.numConfig?.val ?? 10);
          break;
        default:
          return null;
      }

      return options;
    })
    .filter((item): item is Rule & { rawType?: string } => item !== null);
}
